import React, { useState, useEffect, useRef } from 'react';
import { Users, Shield, UserCog, BarChart3, Search, Plus, X, AlertCircle } from 'lucide-react';
import { roleManagementApi } from '../services/api';
import { showSuccess, showError, confirmAsync } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';
import {
  Role,
  UserWithRoles,
  Permission,
  RoleAnalytics,
} from '../types';

type TabType = 'users' | 'roles' | 'permissions' | 'analytics';

export default function AdminRoleManagement() {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users tab state
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Roles tab state
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [analytics, setAnalytics] = useState<RoleAnalytics | null>(null);

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  // Modal ref for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(usersSearch);
      setUsersPage(1); // Reset to page 1 when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [usersSearch]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setUsersPage(1);
  }, [usersRoleFilter]);

  // Load data based on active tab
  useEffect(() => {
    loadTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usersPage, debouncedSearch, usersRoleFilter]);

  // Focus management for modal
  useEffect(() => {
    if (showAssignModal && modalRef.current) {
      // Store previous focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus first focusable element in modal (the select dropdown)
      const firstFocusable = modalRef.current.querySelector<HTMLElement>(
        'select, button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Return focus on cleanup
      return () => {
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [showAssignModal]);

  const loadTabData = async () => {
    try {
      setLoading(true);
      setError(null);

      switch (activeTab) {
        case 'users':
          await loadUsers();
          await loadRoles(); // For filters
          break;
        case 'roles':
          await loadRoles();
          break;
        case 'permissions':
          await loadPermissions();
          break;
        case 'analytics':
          await loadAnalytics();
          break;
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Error loading data:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    const params: {
      page: number;
      page_size: number;
      search?: string;
      role_filter?: string;
    } = {
      page: usersPage,
      page_size: 50,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (usersRoleFilter) params.role_filter = usersRoleFilter;

    const response = await roleManagementApi.getUsersWithRoles(params);
    // Ensure response has the expected structure
    const items = Array.isArray(response.items) ? response.items : [];
    setUsers(items);
    setUsersTotalPages(response.total_pages || 1);
  };

  const loadRoles = async () => {
    const response = await roleManagementApi.getAllRoles({
      include_user_counts: true,
      limit: 100,
    });
    const rolesList = Array.isArray(response) ? response : [];
    setRoles(rolesList);
  };

  const loadPermissions = async () => {
    const response = await roleManagementApi.getPermissions();
    setPermissions(response);
  };

  const loadAnalytics = async () => {
    const response = await roleManagementApi.getRoleAnalytics();
    setAnalytics(response);
  };

  // Handle keyboard navigation for modal focus trap
  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'select, button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    } else if (e.key === 'Escape') {
      setShowAssignModal(false);
      setSelectedUserId(null);
      setSelectedRoleId(null);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUserId || !selectedRoleId) return;

    try {
      await roleManagementApi.assignRole({
        user_id: selectedUserId,
        role_id: selectedRoleId,
      });
      setShowAssignModal(false);
      setSelectedUserId(null);
      setSelectedRoleId(null);
      loadUsers();

      const role = roles.find(r => r.id === selectedRoleId);
      const user = users.find(u => u.id === selectedUserId);
      showSuccess('Role assigned successfully', {
        description: role && user ? `${role.name} assigned to ${user.display_name || user.email}` : undefined,
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Error assigning role:', apiError);
      showError('Failed to assign role', {
        description: apiError.message,
      });
    }
  };

  const handleRevokeRole = async (userId: number, roleId: number, userName: string, roleName: string) => {
    const confirmed = await confirmAsync(`Remove ${roleName} role from ${userName}?`, {
      description: 'This action can be reversed by reassigning the role.',
      confirmLabel: 'Remove Role',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await roleManagementApi.revokeRole(userId, roleId);
      loadUsers();
      showSuccess('Role revoked successfully', {
        description: `${roleName} removed from ${userName}`,
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Error revoking role:', apiError);
      showError('Failed to revoke role', {
        description: apiError.message,
      });
    }
  };

  const renderUsersTab = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                data-testid="user-search-input"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Role</label>
            <select
              value={usersRoleFilter}
              onChange={(e) => setUsersRoleFilter(e.target.value)}
              data-testid="role-filter-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.name}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => { setUsersSearch(''); setUsersRoleFilter(''); }}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.display_name || user.email}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-2">
                    {user.roles.length === 0 ? (
                      <span className="text-sm text-gray-400">No roles</span>
                    ) : (
                      user.roles.map((role) => (
                        <div
                          key={role.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-primary-dark text-xs font-medium rounded-full"
                          data-testid="role-badge"
                        >
                          {role.name}
                          <button
                            onClick={() => handleRevokeRole(user.id, role.id, user.email, role.name)}
                            className="ml-1 hover:text-red-600"
                            aria-label={`Revoke role ${role.name} from ${user.display_name || user.email}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.email_verified ? (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Verified
                    </span>
                  ) : (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Unverified
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedUserId(user.id);
                      setShowAssignModal(true);
                    }}
                    className="text-secondary hover:text-teal-900 inline-flex items-center gap-1"
                    aria-label={`Assign role to ${user.display_name || user.email}`}
                  >
                    <Plus className="h-4 w-4" />
                    Assign Role
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {usersTotalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setUsersPage(p => Math.max(1, p - 1))}
            disabled={usersPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {usersPage} of {usersTotalPages}
          </span>
          <button
            onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
            disabled={usersPage === usersTotalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const renderRolesTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <div
            key={role.id}
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
            data-testid={`role-card-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-testid="role-name">
                  <Shield className="h-5 w-5 text-secondary" />
                  {role.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                {role.is_system && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-moonbone text-ink rounded">
                    System Role
                  </span>
                )}
              </div>
            </div>
            {role.user_count !== undefined && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-900">{role.user_count}</span> users with this role
                </p>
              </div>
            )}
            {role.permissions && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Permissions:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(role.permissions).filter(([, value]) => value).map(([key]) => (
                    <span key={key} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                      {key}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderPermissionsTab = () => {
    // Group permissions by category
    const groupedPermissions = permissions.reduce((acc, perm) => {
      if (!acc[perm.category]) {
        acc[perm.category] = [];
      }
      acc[perm.category].push(perm);
      return acc;
    }, {} as Record<string, Permission[]>);

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900" data-testid="permissions-heading">System Permissions</h2>
        {Object.entries(groupedPermissions).map(([category, perms]) => (
          <div key={category} className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{category}</h3>
            <div className="space-y-3">
              {perms.map((perm) => (
                <div key={perm.key} className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                  <div className="flex-shrink-0 mt-0.5">
                    <Shield className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{perm.key}</p>
                    <p className="text-sm text-gray-600">{perm.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAnalyticsTab = () => {
    if (!analytics) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="analytics-card bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_users}</p>
              </div>
            </div>
          </div>
          <div className="analytics-card bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-sm text-gray-600">With Roles</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.users_with_roles}</p>
              </div>
            </div>
          </div>
          <div className="analytics-card bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">Without Roles</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.users_without_roles}</p>
              </div>
            </div>
          </div>
          <div className="analytics-card bg-white p-6 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Changes (7d)</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.recent_changes_7d}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Role Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Distribution</h3>
          <div className="space-y-3">
            {analytics.role_distribution.map((dist) => (
              <div key={dist.role_id}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{dist.role_name}</span>
                  <span className="text-sm text-gray-600">{dist.user_count} users</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full"
                    style={{
                      width: `${(dist.user_count / analytics.total_users) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UserCog className="h-6 w-6 text-secondary" />
          Role Management
        </h1>
        <p className="text-gray-600 mt-1">Manage user roles and permissions</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-8 px-6" role="tablist">
            {[
              { id: 'users', label: 'Users', icon: Users },
              { id: 'roles', label: 'Roles', icon: Shield },
              { id: 'permissions', label: 'Permissions', icon: Shield },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            ].map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary text-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading && (
            <div data-testid="loading-spinner" className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          )}

          {error && (
            <div data-testid="error-message" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadTabData}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && (
            <div data-testid="content-loaded">
              {activeTab === 'users' && renderUsersTab()}
              {activeTab === 'roles' && renderRolesTab()}
              {activeTab === 'permissions' && renderPermissionsTab()}
              {activeTab === 'analytics' && renderAnalyticsTab()}
            </div>
          )}
        </div>
      </div>

      {/* Assign Role Modal */}
      {showAssignModal && (
        <div
          ref={modalRef}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assign-role-heading"
          data-testid="assign-role-modal"
          onKeyDown={handleModalKeyDown}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 id="assign-role-heading" className="text-lg font-semibold text-gray-900 mb-4" data-testid="assign-role-heading">Assign Role</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Role
                </label>
                <select
                  name="role"
                  data-testid="role-select"
                  value={selectedRoleId || ''}
                  onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choose a role...</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedUserId(null);
                    setSelectedRoleId(null);
                  }}
                  data-testid="assign-role-cancel"
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignRole}
                  disabled={!selectedRoleId}
                  data-testid="assign-role-confirm"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
